// @ts-strict-ignore
import { useCallback, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useTheme } from '@mui/material';
import type { Theme } from '@mui/material';
import useWindowSize from '../services/useWindowSize.ts';
import getParentSizeFromRef from '../services/getParentSizeFromRef.ts';
import type { BestPractice, BestPracticeWithExtremes } from '../types';

type Data = {
    rating?: number | null;
    limits: BestPracticeWithExtremes | BestPractice;
};

export type GaugeChartProps = {
    data: Data;
    showLabels?: boolean;
    showMinMax?: boolean;
};

export function GaugeChart({
    data,
    showLabels = false,
    showMinMax = false,
}: GaugeChartProps) {
    const {
        rating,

        /*anonymous,*/
        limits,
    } = data;

    const theme = useTheme();
    const svgRef = useRef<SVGSVGElement>(null);
    const windowSize = useWindowSize();

    const render = useCallback(() => {
        if (!svgRef.current) {
            return;
        }
        const svg = d3.select<SVGSVGElement, unknown>(svgRef.current);
        svg.selectAll('g').remove();

        const parentSize = getParentSizeFromRef(svgRef);
        if (!parentSize) {
            return;
        }
        //Dont Display anything if rating is a "NaN" or null
        if (rating == null || Number.isNaN(rating)) {
            //&& anonymous === false) {
            return;
        }

        const { parentWidth, parentHeight } = parentSize;
        const ratingRounded = Math.round(rating);

        draw(svg, theme, {
            rating: ratingRounded,
            strokeWidth: Math.max(4, parentHeight * 0.045),
            showLabels,
            limits: {
                // todo We should remove min&max, but currently there is no guarantee that these are passed
                min: 40,
                max: 100,
                ...limits,
            },
            width: parentWidth * 4,
            height: parentHeight * 4,
            showMinMax,
        });

        // svg.append('g')
        //     .append('text')
        //     .attr('x', parentWidth / 2)
        //     .attr('y', parentHeight / 2 + (showLabels ? 20 : 0))
        //     .attr('dy', '0.35em')
        //     .attr('font-family', 'bariolthin')
        //     .attr(
        //         'font-size',
        //         //parentHeight * (parentHeight <= 145 ? 0.35 : 0.4),
        //         parentHeight *
        //             (parentHeight >= 160
        //                 ? 0.4
        //                 : parentHeight <= 65
        //                 ? 0.25
        //                 : 0.3),
        //     )
        //     .attr('font-weight', 'normal')
        //     .attr('text-anchor', 'middle')
        //     .text(ratingRounded)
        //     .attr('fill', theme.bino.color.black);
    }, [theme, rating, limits, showLabels, showMinMax]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: We need windowSize to redraw on resize
    useEffect(() => {
        render();
    }, [render, windowSize]);

    return (
        <svg
            ref={svgRef}
            width="100%"
            height="100%"
            overflow="visible"
            preserveAspectRatio="xMidYMid"
        />
    );
}

/** @eee https://en.wikipedia.org/wiki/Sagitta_(geometry) */
const sagitta = (r: number, L: number) => r - Math.sqrt(r * r - L * L);

const between = (v: number, min: number, max: number) =>
    Math.min(max, Math.max(min, v));

function draw(
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    theme: Theme,
    props: {
        rating: number;
        strokeWidth: number;
        showLabels: boolean;
        width: number;
        height: number;
        showMinMax: boolean;
        boldScore?: boolean;
        limits: BestPracticeWithExtremes;
    },
) {
    const { limits } = props;

    const tau = 2 * Math.PI; // http://tauday.com/tau-manifesto

    // An arc function with all values bound except the endAngle. So, to compute an
    // SVG path string for a given angle, we pass an object with an endAngle
    // property to the `arc` function, and it will return the corresponding string.
    // Get the SVG container, and apply a transform such that the origin is the
    // center of the canvas. This way, we don’t need to position arcs individually.
    const outerWidth = props.width * 0.25,
        outerHeight = props.height * 0.25,
        width = outerWidth - props.strokeWidth * Math.SQRT2,
        height = outerHeight - props.strokeWidth * Math.SQRT2,
        extraSpaceForLabels = 15,
        size =
            Math.min(width, height) -
            (props.showLabels ? 0 : extraSpaceForLabels),
        radius = size / 2,
        lineSize = size * 0.067;

    const g = svg
        .append('g')
        .attr(
            'transform',
            'translate(' +
                outerWidth / (props.showLabels || outerWidth >= 100 ? 2 : 2.2) +
                ',' +
                (outerHeight / 2 +
                    (props.showLabels ? extraSpaceForLabels : 0)) +
                ')',
        );

    const gap = 0.2; // Gap in the circle (limits min and max angle)
    const convertPercentToAngle = (percent: number) => {
        let p = percent;
        if (limits.min > 0 || limits.max < 100) {
            p = p < limits.min ? limits.min : p;
            p = p > limits.max ? limits.max : p;
            p = (p - limits.min) * (100 / (limits.max - limits.min));
        }
        return Math.PI + (gap * 0.5 + (p / 100) * (1 - gap)) * tau;
    };

    const arc = d3
        .arc() // .innerRadius(size / 2 - props.strokeWidth)
        .innerRadius(radius - size * 0.067) //chart line width shall be 6.7% of the width of the circle
        .outerRadius(radius) //.cornerRadius(props.strokeWidth / 2)
        .cornerRadius(radius * 0.1)
        .startAngle(convertPercentToAngle(0));

    // const arcSquare = d3.arc()
    //     .innerRadius(size / 2 - props.strokeWidth)
    //     .outerRadius(size / 2)
    //     .cornerRadius(0)
    //     .startAngle(convertPercentToAngle(0));

    const bgColor =
        theme.palette.mode === 'dark'
            ? theme.bino.color.darkerGray
            : theme.bino.color.lightGrayChart;
    const fillColor =
        limits.P90 == null
            ? theme.bino.color.purple
            : props.rating >= limits.P90
              ? theme.bino.color.darkGreen
              : props.rating >= limits.P75
                ? theme.bino.color.green
                : props.rating >= limits.P25
                  ? theme.bino.color.yellow
                  : props.rating >= limits.P10
                    ? theme.bino.color.orange
                    : theme.bino.color.red;

    //start debugging
    // g.append('circle')
    //     .attr('cx', 0)
    //     .attr('cy', 0)
    //     .attr('r', radius)
    //     .style('stroke', theme.bino.color.black)
    //     .style('fill', theme.bino.color.white)
    //     .style('stroke-dasharray', '3, 3');
    //end debugging

    // Add the background arc, from 0 to 100% (tau).
    g.append('path')
        .datum({
            endAngle: convertPercentToAngle(100),
        })
        .attr('d', arc)
        .attr('fill', bgColor);

    // Add the foreground arc
    if (!Number.isNaN(props.rating)) {
        /**
         * The minimum rating that we can visually render in a nice way is slightly higher than the
         * actual min rating. Otherwise, we get an ugly effect and completely lose the rounded border
         */
        const minRatingForArc = limits.min + 2;
        g.append('path')
            .datum({
                endAngle: convertPercentToAngle(
                    props.rating <= minRatingForArc
                        ? minRatingForArc
                        : props.rating,
                ),
            })
            .attr('d', arc)
            .style('fill', fillColor);

        g.append('text')
            .attr('x', 0)
            .attr('y', 0)
            .text(props.rating)
            .attr('dy', '0.35em')
            .attr('font-family', 'bariolthin')
            .attr('font-weight', props.boldScore ? 'bold' : 'normal')
            .attr('font-size', radius * 0.9)
            .attr('text-anchor', 'middle')
            .attr(
                'fill',
                props.rating < limits.P10
                    ? theme.bino.color.red
                    : theme.palette.text.primary,
            );
    }

    const labels = ['P10', 'P25', 'P75 (BP)', 'P90'];
    [limits.P10, limits.P25, limits.P75, limits.P90].forEach(
        (percentage, i) => {
            if (percentage == null) {
                return;
            }

            const height = lineSize,
                //props.strokeWidth;// * 1.3;//1.08;
                fontSizeSmall = 8,
                fontSizeMedium = 10,
                fontSizeLarge = 12;
            // Limit so that we don't render the min and max bumps over the edge
            const limit = height / radius;

            // If the rating is larger the p, we render p in the color of the rating.
            // But it becomes ugly if the triangle switches color when the rating bar
            // has not yet completely passed the bump, so offset the bumps slightly to
            // the left.
            const t =
                -Math.PI * 0.5 +
                between(
                    convertPercentToAngle(percentage), //- limit,
                    convertPercentToAngle(0) + limit,
                    convertPercentToAngle(100) - limit,
                );

            const degrees = ((t / tau) * 360) % 360;
            const r = size / 2;

            // const cornerRadius = c / 12;
            const cos = Math.cos(t);
            const sin = Math.sin(t);
            const x = r * cos;
            const y = r * sin;

            const triangle = d3.path();

            const s = sagitta(radius, height);
            const sideLength = (2 / Math.sqrt(3)) * height;

            triangle.moveTo(-sideLength / 2, percentage === 0 ? s * 3 : s / 3);
            triangle.lineTo(0, -height); // todo Maybe curve this
            triangle.lineTo(sideLength / 2, percentage === 100 ? s * 3 : s / 3);
            triangle.quadraticCurveTo(
                0,
                0,
                -sideLength / 2,
                percentage === 0 ? s * 3 : s / 3,
            );

            g.append('path')
                .attr('d', triangle.toString())
                .attr(
                    'transform',
                    `translate(${x ? x : 0}, ${y ? y : 0}) rotate(${
                        90 + (degrees ? degrees : 0)
                    }, 0, 0) `,
                )
                .style(
                    'fill',
                    props.rating >= percentage ? fillColor : bgColor,
                );

            if (props.showLabels || i === 2) {
                // If showLabels is true, it trumps showBP
                const label = props.showLabels ? labels[i] : 'BP';

                //arbitrary font size according to size
                let fontSize = fontSizeSmall;

                if (size >= 100 && size < 150) {
                    fontSize = fontSizeMedium;
                } else if (size >= 150) {
                    fontSize = fontSizeLarge;
                }

                g.append('text')
                    .attr('fill', theme.palette.text.primary)
                    .attr('font-family', 'AvertaStandard')
                    .attr('font-size', fontSize)
                    .attr('x', (radius + height * 1.15) * cos)
                    .attr('y', (radius + height * 1.15) * sin)
                    .attr(
                        'dominant-baseline',
                        sin < -0.5
                            ? 'baseline'
                            : sin > 0.5
                              ? 'hanging'
                              : 'middle',
                    )
                    .style(
                        'text-anchor',
                        cos < -0.5 ? 'end' : cos > 0.5 ? 'start' : 'middle',
                    )
                    .text(label);
            }
        },
    );

    if (props.showMinMax) {
        [limits.min, limits.max].forEach((value) => {
            const t = -Math.PI * 0.5 + convertPercentToAngle(value);
            const cos = Math.cos(t);
            const sin = Math.sin(t);
            g.append('text')
                .attr('fill', `${theme.palette.text.primary}`)
                .attr('font-size', '12')
                .attr('font-weight', 'bold')
                .attr('x', radius * cos)
                .attr('y', (radius + 5) * sin)
                .style('text-anchor', cos < 0 ? 'start' : 'end')
                .text(value.toString());
        });
    }
}

export default GaugeChart;
